import { useState, useEffect, useMemo } from 'react';
import { Activity, Clock, RefreshCw, MessageSquare, PhoneCall, Video as VideoIcon, PieChart, CheckCircle } from 'lucide-react';
import { useExpertActivityTracker } from '../../hooks/expertDashboardHooks/useExpertActivityTracker'; 

export default function ExpertActivityLogsPage() {
  const [expertCode, setExpertCode] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const { stats, history, loading: trackerLoading, error, refresh: refreshTracker } = useExpertActivityTracker(expertCode);

  useEffect(() => {
    const code = localStorage.getItem('expertCode') || localStorage.getItem('expert_code');
    setExpertCode(code);
  }, []);

  const handleRefresh = () => {
    refreshTracker(activeFilter);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilter = e.target.value;
    setActiveFilter(newFilter);
    refreshTracker(newFilter);
  };

  const sessionStats = useMemo(() => {
    const chatSec = (stats?.total_chat_minutes || 0) * 60;
    const callSec = (stats?.total_call_minutes || 0) * 60;
    const videoSec = (stats?.total_video_minutes || 0) * 60;
    const totalSec = chatSec + callSec + videoSec;

    return {
      chatSec, callSec, videoSec, totalSec,
      chatPct: totalSec ? (chatSec / totalSec) * 100 : 0,
      callPct: totalSec ? (callSec / totalSec) * 100 : 0,
      videoPct: totalSec ? (videoSec / totalSec) * 100 : 0,
    };
  }, [stats]);

  const formatDuration = (totalSeconds: number | null) => {
    if (!totalSeconds) return '0m';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  if (trackerLoading && !stats) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl shadow-sm border border-slate-100 min-h-[500px]">
        <div className="w-10 h-10 border-4 border-slate-100 border-t-emerald-500 rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500">Loading activity logs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl shadow-sm border border-slate-100 min-h-[500px]">
        <p className="text-red-500 bg-red-50 px-4 py-2 rounded-lg">{error}</p>
        <button onClick={handleRefresh} className="mt-4 text-emerald-600 hover:underline">Try Again</button>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col p-4 md:p-8 bg-white rounded-xl shadow-sm border border-slate-100 min-h-[500px] gap-6">
      
      {/* ── HEADER ── */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b border-slate-100 pb-6 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
            <Activity className="w-7 h-7 text-emerald-500" />
          </div>
          <div className="text-left">
            <h2 className="text-2xl font-bold text-slate-800">Activity Logs</h2>
            <p className="text-sm text-slate-500">Track your online presence and session breakdown.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select 
            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 outline-none focus:border-emerald-500 cursor-pointer"
            value={activeFilter}
            onChange={handleFilterChange}
          >
            <option value="today">Today</option>
            <option value="7days">Last 7 Days</option>
            <option value="1month">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>

        <button
  onClick={handleRefresh}
  disabled={trackerLoading}
  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg transition-all text-sm font-semibold shadow-sm"
>
  <RefreshCw
    className={`w-4 h-4 ${trackerLoading ? "animate-spin" : ""}`}
  />
  {trackerLoading ? "Refreshing..." : "Refresh"}
</button>
        </div>
      </div>

      {/* ── TOP STATS ROW ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-semibold uppercase tracking-wider mb-1">Total Online Today</p>
            <p className="text-3xl font-extrabold text-slate-800">{stats?.today.formatted || '0h 0m'}</p>
          </div>
          <Clock className="w-10 h-10 text-slate-300" />
        </div>
        <div className="p-5 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-semibold uppercase tracking-wider mb-1">Total Online This Week</p>
            <p className="text-3xl font-extrabold text-slate-800">{stats?.thisWeek.formatted || '0h 0m'}</p>
          </div>
          <Clock className="w-10 h-10 text-slate-300" />
        </div>
        <div className="p-5 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 font-semibold uppercase tracking-wider mb-1">
              {activeFilter === 'all' ? 'Total Lifetime Orders' : 'Period Orders'}
            </p>
            <p className="text-3xl font-extrabold text-slate-800">{stats?.total_orders || 0}</p>
          </div>
          <CheckCircle className="w-10 h-10 text-emerald-400" />
        </div>
      </div>

      {/* ── MIDDLE ROW: GRAPH & SESSION BREAKDOWN ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-slate-50 rounded-xl border border-slate-100 p-6 flex flex-col items-center justify-center">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4 w-full text-left flex items-center gap-2">
            <PieChart className="w-4 h-4" /> {activeFilter === 'all' ? 'All-Time Distribution' : 'Period Distribution'}
          </h3>
          <div className="relative w-40 h-40">
            {sessionStats.totalSec === 0 ? (
              <div className="w-full h-full rounded-full border-8 border-slate-200 flex items-center justify-center text-slate-400 text-sm font-bold">No Data</div>
            ) : (
              <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
                <path className="text-slate-200" strokeWidth="4" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path stroke="#3B82F6" strokeWidth="4" strokeDasharray={`${sessionStats.chatPct}, 100`} fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path stroke="#10B981" strokeWidth="4" strokeDasharray={`${sessionStats.callPct}, 100`} strokeDashoffset={`-${sessionStats.chatPct}`} fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                <path stroke="#8B5CF6" strokeWidth="4" strokeDasharray={`${sessionStats.videoPct}, 100`} strokeDashoffset={`-${sessionStats.chatPct + sessionStats.callPct}`} fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
              </svg>
            )}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-slate-800">{formatDuration(sessionStats.totalSec)}</span>
              <span className="text-[10px] text-slate-500 uppercase font-bold">Total Talk Time</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <MessageSquare className="w-5 h-5" /><span className="font-bold text-sm uppercase tracking-wider">Chat Time</span>
            </div>
            <p className="text-3xl font-extrabold text-blue-700">{formatDuration(sessionStats.chatSec)}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-emerald-600 mb-2">
              <PhoneCall className="w-5 h-5" /><span className="font-bold text-sm uppercase tracking-wider">Call Time</span>
            </div>
            <p className="text-3xl font-extrabold text-emerald-700">{formatDuration(sessionStats.callSec)}</p>
          </div>
          <div className="bg-purple-50 border border-purple-100 rounded-xl p-5 flex flex-col justify-center">
            <div className="flex items-center gap-2 text-purple-600 mb-2">
              <VideoIcon className="w-5 h-5" /><span className="font-bold text-sm uppercase tracking-wider">Video Time</span>
            </div>
            <p className="text-3xl font-extrabold text-purple-700">{formatDuration(sessionStats.videoSec)}</p>
          </div>
        </div>
      </div>

      {/* ── LOGS (Strictly Last Offline Only) ── */}
      <div className="flex-1 flex flex-col mt-4">
        <h3 className="text-lg font-bold text-slate-800 mb-4 text-left">Logs</h3>
        <div className="pr-2">
          {history.length === 0 ? (
            <div className="py-6 text-center text-slate-500 bg-slate-50 rounded-xl border border-slate-100">No logs found.</div>
          ) : (
            (() => {
              const offlineSession = history.find(session => session.ended_at !== null || session.is_online === false);
              
              if (!offlineSession) {
                return (
                  <div className="py-6 text-center text-slate-500 bg-slate-50 rounded-xl border border-slate-100">
                    No offline records found yet.
                  </div>
                );
              }

              const offlineTime = offlineSession.ended_at || offlineSession.started_at;
              
              return (
                <div className="flex items-center justify-between p-4 rounded-xl border border-slate-100 bg-white shadow-sm">
                  
                  {/* 🟢 Left side: Just the title and icon */}
                  <div className="flex items-center gap-4">
                    <div className="relative flex items-center justify-center w-8 h-8 rounded-full bg-slate-50">
                      <span className="w-3 h-3 rounded-full bg-slate-400"></span>
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-bold text-slate-800">Last Offline</p>
                    </div>
                  </div>

                  {/* 🟢 Right side: The date and time using flex justify-between */}
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-500">
                      {new Date(offlineTime).toLocaleDateString()} at {new Date(offlineTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  
                </div>
              );
            })()
          )}
        </div>
      </div>
    </div>
  );
}